import {
  Box,
  Card,
  Divider,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { Flexbox } from '../../../../components/layout/Flexbox';
import { getDeductibleLanguage } from '../../../../i18n/utils';
import {
  FORMAT_DATE_AND_TIME,
  toUniversalUtcDate,
} from '../../../../utils/date.utils';
import { CurrencyAmount } from '@cowbell/ui/core';
import type { ProductType } from '../../../../types';

interface MTESummaryProps {
  readonly endorsement: {
    readonly aggLimit: number;
    readonly deductible: number;
    readonly endDate: number;
    readonly effectiveDate: number;
    readonly product: ProductType;
    readonly totalPremium: number;
  };
  /** ISO 4217 code from the policy this endorsement amends. */
  readonly currency: string | null | undefined;
}

export const MTESummary = ({ endorsement, currency }: MTESummaryProps) => {
  const { palette } = useTheme();

  const formattedEndorsementDetails = composeFormattedDetails({
    effectiveDate: endorsement.effectiveDate,
    endDate: endorsement.endDate,
  });

  return (
    <SummaryCard square>
      <Box>
        <Typography variant="subtitle2" color="primary">
          MTE SUMMARY
        </Typography>
      </Box>
      <Divider style={{ width: '100%', margin: '0.5rem 0 1rem 0' }} />
      <Box>
        <Flexbox>
          <Box width="50%" marginRight={1}>
            <Flexbox>
              <Typography variant="caption">Aggregate Limit</Typography>
              <Typography variant="caption">
                <CurrencyAmount
                  amount={endorsement.aggLimit}
                  currency={currency}
                  notation="compact"
                  minimumFractionDigits={0}
                  maximumFractionDigits={1}
                />
              </Typography>
            </Flexbox>
            <Flexbox>
              <Typography variant="caption">
                {getDeductibleLanguage(endorsement.product)}
              </Typography>
              <Typography variant="caption">
                <CurrencyAmount
                  amount={endorsement.deductible}
                  currency={currency}
                  notation="compact"
                  minimumFractionDigits={0}
                  maximumFractionDigits={1}
                />
              </Typography>
            </Flexbox>
            <Flexbox>
              <Typography
                variant="caption"
                style={{ color: palette.text.text12 }}
              >
                Premium Delta
              </Typography>
              <Typography variant="caption">
                <CurrencyAmount
                  amount={endorsement.totalPremium}
                  currency={currency}
                />
              </Typography>
            </Flexbox>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box width="50%" marginLeft={1}>
            <Flexbox>
              <Typography variant="caption">Endorsement Start Date</Typography>
              <Typography variant="caption">
                {formattedEndorsementDetails.uiEffectiveDate}
              </Typography>
            </Flexbox>
            <Flexbox>
              <Typography variant="caption">Endorsement End Date</Typography>
              <Typography variant="caption">
                {formattedEndorsementDetails.uiEndDate}
              </Typography>
            </Flexbox>
          </Box>
        </Flexbox>
      </Box>
    </SummaryCard>
  );
};

const SummaryCard = styled(Card)(({ theme: { palette } }) => ({
  padding: '1rem',
  backgroundColor: palette.background.default,
  marginBottom: '1rem',
}));

const composeFormattedDetails = ({
  effectiveDate,
  endDate,
}: {
  effectiveDate: number;
  endDate: number;
}) => {
  const uiEffectiveDate = toUniversalUtcDate(effectiveDate, {
    format: FORMAT_DATE_AND_TIME,
  });

  const uiEndDate = toUniversalUtcDate(endDate, {
    format: FORMAT_DATE_AND_TIME,
  });

  return {
    uiEffectiveDate,
    uiEndDate,
  };
};
